import { asCategoryId, asItemId, asTodoId, between, newId } from "@pack-it-up/shared";
import { categoryKind } from "../entities/category.js";
import { snapshotOf } from "../entities/provenance.js";
import { memberFromParticipant } from "../entities/template.js";
import { emptyCompletion } from "./completion.js";
import { resolve } from "./relativeOffset.js";
// trim, casefold, collapse internal whitespace
export const normalizeName = (s) => s.trim().toLowerCase().replace(/\s+/g, " ");
// The normalised identity used to merge a template row into an existing trip row
// (Doc 07 §3); sync-back reuses it as its fallback match (Doc 13 §2.1).
export const dedupeKey = (name, description, notes) => [normalizeName(name), normalizeName(description ?? ""), normalizeName(notes ?? "")].join("\u0000");
const seedRow = (id, content, provenance, templateIds) => ({
    isNew: false,
    id,
    sources: provenance ? [...provenance.templateSources] : [],
    templateIds: templateIds ? [...templateIds] : [],
    createdByImport: provenance?.createdByImport ?? false,
    importedSnapshot: provenance?.importedSnapshot ?? content,
    modified: provenance?.modifiedSinceImport ?? false,
    changed: false,
});
const addSource = (row, templateId, templateItemId, templateName) => {
    if (row.sources.some((s) => s.templateId === templateId && s.templateItemId === templateItemId))
        return; // already claimed — re-import is idempotent
    row.sources = [...row.sources, { templateId, templateItemId, ...(templateName !== undefined ? { templateName } : {}) }];
    if (!row.templateIds.includes(templateId))
        row.templateIds = [...row.templateIds, templateId];
    row.changed = true;
};
const provenanceOf = (row) => ({
    templateSources: row.sources,
    createdByImport: row.createdByImport,
    importedSnapshot: row.importedSnapshot,
    modifiedSinceImport: row.modified,
});
export const resolveTemplateParticipants = (trip, templates, actor) => {
    const actorEntry = trip.members[actor];
    const canAddMembers = actorEntry?.kind === "user" && actorEntry.role === "owner";
    const memberCreates = [];
    const participantIds = new Map();
    const managedByName = new Map();
    for (const [id, m] of Object.entries(trip.members)) {
        if (m.kind === "managed")
            managedByName.set(normalizeName(m.profile.displayName), id);
    }
    for (const tpl of templates) {
        for (const { id, participant } of tpl.participants ?? []) {
            if (participantIds.has(id))
                continue;
            const existing = participant.kind === "user"
                ? (trip.members[participant.userId] ? participant.userId : undefined)
                : managedByName.get(normalizeName(participant.displayName));
            if (existing !== undefined) {
                participantIds.set(id, existing);
                continue;
            }
            if (!canAddMembers)
                continue;
            const target = participant.kind === "user" ? participant.userId : id;
            participantIds.set(id, target);
            if (participant.kind === "managed")
                managedByName.set(normalizeName(participant.displayName), target);
            memberCreates.push({ participantId: target, entry: memberFromParticipant(participant, actor) });
        }
    }
    return { participantIds, memberCreates };
};
// A `specific` assignment names template participants; the row lands on the trip
// naming the members they resolved to. An allocation whose participant didn't make it
// (a non-owner's import) is dropped, and a row left with none falls back to "everyone"
// rather than becoming a row nobody can ever check off.
export const makeAssignmentResolver = (trip, resolution) => (a) => {
    if (a.kind !== "specific")
        return a;
    const allocations = a.allocations.flatMap((alloc) => {
        const participantId = resolution.participantIds.get(alloc.participantId) ?? alloc.participantId;
        return trip.members[participantId] ||
            resolution.memberCreates.some((m) => m.participantId === participantId)
            ? [{ ...alloc, participantId }]
            : [];
    });
    return allocations.length > 0 ? { kind: "specific", allocations } : { kind: "everyone" };
};
// The key a trip row is claimed under by a template row it was imported from
// (`provenance.templateSources`). Matching on it is what lets a refresh — or any
// re-import — recognise a row either side has since renamed (Doc 14 §2).
export const sourceKey = (templateId, templateItemId) => `${templateId} ${templateItemId}`;
export const importTemplates = (trip, templates, current, actor, idGen = newId) => {
    const parentId = trip.id;
    const resolution = resolveTemplateParticipants(trip, templates, actor);
    const { memberCreates } = resolution;
    const resolveAssignment = makeAssignmentResolver(trip, resolution);
    const participantIds = resolution.participantIds;
    // Issue #188 — the template row's staging, re-keyed onto the trip's members. Follows
    // resolveAssignment's rule: an entry for a participant the import didn't bring across
    // is dropped rather than left pointing at nobody. A `shared` row is a single collapsed
    // record (`anyDone`), and the participant who staged it on the template need not be on
    // the trip at all, so it is re-recorded under the importer — the box stays ticked and
    // whoever unticks it clears one entry, exactly as a trip-side shared toggle does.
    const resolveStaging = (staging, assignment) => {
        if (staging === undefined)
            return undefined;
        const entries = Object.entries(staging.byParticipant);
        const done = entries.filter(([, e]) => e.done);
        if (done.length === 0)
            return undefined;
        if (assignment.kind === "shared") {
            return { byParticipant: { [actor]: { done: true, checkedBy: actor, at: new Date() } } };
        }
        const byParticipant = {};
        for (const [pid, entry] of done) {
            const target = participantIds.get(pid) ?? pid;
            if (!trip.members[target] && !memberCreates.some((m) => m.participantId === target))
                continue;
            byParticipant[target] = { ...entry, checkedBy: actor };
        }
        return Object.keys(byParticipant).length > 0 ? { byParticipant } : undefined;
    };
    // --- categories: merge by normalised name + kind (categories are typed per tab —
    // packing items and todos never share a trip category; legacy categories without a
    // kind count as "packing"), new ones appended after existing ---
    const catByName = new Map();
    const catKey = (name, kind) => `${kind} ${normalizeName(name)}`;
    let catCursor = null;
    for (const c of current.categories) {
        catByName.set(catKey(c.name, categoryKind(c)), c.id);
        if (catCursor === null || c.order > catCursor)
            catCursor = c.order;
    }
    const categoryCreates = [];
    const resolveCategory = (displayName, kind) => {
        const key = catKey(displayName, kind);
        const existing = catByName.get(key);
        if (existing)
            return existing;
        const id = asCategoryId(idGen());
        const order = between(catCursor, null);
        catCursor = order;
        categoryCreates.push({ id, parentId, name: displayName, order, collapsedBy: {}, kind });
        catByName.set(key, id);
        return id;
    };
    // --- per-category dedupe indexes + order cursors (items and todos kept apart) ---
    const itemIndex = new Map();
    const todoIndex = new Map();
    const itemOrder = new Map();
    const todoOrder = new Map();
    const indexFor = (map, categoryId) => {
        let inner = map.get(categoryId);
        if (inner == null) {
            inner = new Map();
            map.set(categoryId, inner);
        }
        return inner;
    };
    const bumpOrder = (map, categoryId) => {
        const next = between(map.get(categoryId) ?? null, null);
        map.set(categoryId, next);
        return next;
    };
    const noteOrder = (map, categoryId, order) => {
        const cur = map.get(categoryId) ?? null;
        if (cur === null || order > cur)
            map.set(categoryId, order);
    };
    // Rows already claimed by a template source, keyed by that source (Doc 14 §2). This
    // index is consulted *before* the dedupe key, and is what stops a re-import from
    // duplicating a row either side has renamed since the last import. It spans every
    // category: a claimed row keeps the placement the user gave it.
    const itemBySource = new Map();
    const todoBySource = new Map();
    const claim = (map, row, sources) => {
        for (const s of sources)
            map.set(sourceKey(s.templateId, s.templateItemId), row);
    };
    for (const it of current.items) {
        const row = seedRow(it.id, snapshotOf(it), it.provenance, it.templateIds);
        indexFor(itemIndex, it.categoryId).set(dedupeKey(it.name, it.description, it.notes), row);
        claim(itemBySource, row, row.sources);
        noteOrder(itemOrder, it.categoryId, it.order);
    }
    for (const td of current.todos) {
        const row = seedRow(td.id, snapshotOf(td), td.provenance, td.templateIds);
        indexFor(todoIndex, td.categoryId).set(dedupeKey(td.name, td.description, td.notes), row);
        claim(todoBySource, row, row.sources);
        noteOrder(todoOrder, td.categoryId, td.order);
    }
    const itemCreates = [];
    const todoCreates = [];
    for (const tpl of templates) {
        const catName = new Map(tpl.categories.map((c) => [c.id, c.name]));
        for (const ti of tpl.items) {
            // Source id first (Doc 14 §2): a row this template already created is the same
            // row however it has been renamed or moved since.
            const claimed = itemBySource.get(sourceKey(tpl.templateId, ti.id));
            if (claimed) {
                addSource(claimed, tpl.templateId, ti.id, tpl.name);
                continue;
            }
            const targetCat = resolveCategory(catName.get(ti.categoryId) ?? "Uncategorised", "packing");
            const index = indexFor(itemIndex, targetCat);
            const key = dedupeKey(ti.name, ti.description, ti.notes);
            const hit = index.get(key);
            if (hit) {
                claim(itemBySource, hit, [{ templateId: tpl.templateId, templateItemId: ti.id }]);
                addSource(hit, tpl.templateId, ti.id, tpl.name);
                continue;
            }
            const assignment = resolveAssignment(ti.assignment);
            const staging = resolveStaging(ti.staging, assignment);
            const draft = {
                id: asItemId(idGen()),
                categoryId: targetCat,
                name: ti.name,
                quantity: ti.quantity,
                ...(ti.description !== undefined ? { description: ti.description } : {}),
                ...(ti.notes !== undefined ? { notes: ti.notes } : {}),
                ...(ti.tags !== undefined ? { tags: ti.tags } : {}),
                assignment,
                completions: emptyCompletion(),
                ...(staging !== undefined ? { staging } : {}),
                order: bumpOrder(itemOrder, targetCat),
            };
            const row = {
                isNew: true,
                id: draft.id,
                sources: [{ templateId: tpl.templateId, templateItemId: ti.id, ...(tpl.name !== undefined ? { templateName: tpl.name } : {}) }],
                templateIds: [tpl.templateId],
                createdByImport: true,
                importedSnapshot: snapshotOf(draft),
                modified: false,
                changed: true,
                draft,
            };
            index.set(key, row);
        }
        for (const tt of tpl.todos) {
            const claimed = todoBySource.get(sourceKey(tpl.templateId, tt.id));
            if (claimed) {
                addSource(claimed, tpl.templateId, tt.id, tpl.name);
                continue;
            }
            const targetCat = resolveCategory(catName.get(tt.categoryId) ?? "Uncategorised", "todo");
            const index = indexFor(todoIndex, targetCat);
            const key = dedupeKey(tt.name, tt.description, tt.notes);
            const hit = index.get(key);
            if (hit) {
                claim(todoBySource, hit, [{ templateId: tpl.templateId, templateItemId: tt.id }]);
                addSource(hit, tpl.templateId, tt.id, tpl.name);
                continue;
            }
            // Resolve the due date now when the trip has a start date; otherwise leave it
            // empty and keep the offset for a later recompute (Doc 07 §4, Doc 09 §3).
            let dueDate = trip.startDate;
            if (tt.relativeDueOffset != null && trip.startDate != null)
                dueDate = resolve(trip.startDate, tt.relativeDueOffset);
            const draft = {
                id: asTodoId(idGen()),
                categoryId: targetCat,
                name: tt.name,
                ...(tt.description !== undefined ? { description: tt.description } : {}),
                ...(tt.notes !== undefined ? { notes: tt.notes } : {}),
                ...(tt.tags !== undefined ? { tags: tt.tags } : {}),
                assignment: resolveAssignment(tt.assignment),
                completions: emptyCompletion(),
                ...(dueDate !== undefined ? { dueDate } : {}),
                ...(tt.relativeDueOffset !== undefined ? { relativeDueOffset: tt.relativeDueOffset } : {}),
                dueDateManuallyOverridden: false,
                remindersSentTo: {},
                reminderComplete: false,
                order: bumpOrder(todoOrder, targetCat),
            };
            const row = {
                isNew: true,
                id: draft.id,
                sources: [{ templateId: tpl.templateId, templateItemId: tt.id, ...(tpl.name !== undefined ? { templateName: tpl.name } : {}) }],
                templateIds: [tpl.templateId],
                createdByImport: true,
                importedSnapshot: snapshotOf(draft),
                modified: false,
                changed: true,
                draft,
            };
            index.set(key, row);
        }
    }
    // --- materialise creates and dedupe patches from the working rows ---
    const provenancePatches = [];
    const finalise = (index, collection, creates) => {
        for (const inner of index.values()) {
            for (const row of inner.values()) {
                if (row.isNew && row.draft) {
                    creates.push({ ...row.draft, provenance: provenanceOf(row), templateIds: row.templateIds });
                }
                else if (row.changed) {
                    provenancePatches.push({
                        collection,
                        id: row.id,
                        provenance: provenanceOf(row),
                        templateIds: row.templateIds,
                    });
                }
            }
        }
    };
    finalise(itemIndex, "items", itemCreates);
    finalise(todoIndex, "todos", todoCreates);
    const importedTemplateIds = [...trip.importedTemplateIds];
    for (const tpl of templates)
        if (!importedTemplateIds.includes(tpl.templateId))
            importedTemplateIds.push(tpl.templateId);
    return {
        categoryCreates,
        itemCreates,
        todoCreates,
        provenancePatches,
        memberCreates,
        tripPatch: { importedTemplateIds },
    };
};
//# sourceMappingURL=importTemplates.js.map